import { useLocation } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, IconButton } from '@mui/material';
import SquaresDashboardHandler from 'handlers/squaresHandlers/squaresDashboard.handler';
import SurvivalAnalysisOverviewHandler from 'handlers/survivalAnalysisHandlers/survivalAnalysisOverview.handler';
import useAppTheme from 'hooks/useAppTheme';
import { useEffect, useMemo, useState } from 'react';
import { selectLatestTagData } from 'store/device.slice';
import { useAppSelector } from 'store/hook';
import { selectNotificationsData } from 'store/notification.slice';
import { type ServiceView } from 'types/survivalAnalysis.types';
import {
  processAssetsWithLiveData,
  processDataAssets,
} from 'views/SurvivalAnalysis/Helper/Function/asset.helper.function';
import { ThemePalette } from 'views/SurvivalAnalysis/Helper/Icons/theme.squares';
import OverviewModalDetails from 'views/SurvivalAnalysis/Overview/modals/Overview-modal-details';
import { AssetsViewV2 } from '../CostAnalysis/Assets/AssetsViewV2';
import { MaintenanceComparisonView } from '../CostAnalysis/MaintenanceComparison/MaintenanceComparisonView';
import { PartsViewV2 } from '../CostAnalysis/Parts/PartsViewV2';
import AssetDetailsView from './Components/AssetDetailsView';
import { MaintenanceDashboardSection } from './Components/MaintainenceDashboardSection';
import UploadWorkOrderComponent from './Components/UploadWorkOrderView';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

export interface PmVsCmApiResponse {
  category: string;
  total_quantity: string;
  total_cost: number;
  total_hours: string;
}

export const DashbaordEntityManagementViewV2 = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const survivalAnalysisHandler = new SurvivalAnalysisOverviewHandler();
  const squaresHandler = new SquaresDashboardHandler();
  const accessToken = useAppSelector((state) => state.authReducer?.accessToken);
  const latestTagData = useAppSelector(selectLatestTagData);
  const notificationsData = useAppSelector(selectNotificationsData);
  const assets = useAppSelector((state) => state.assetReducer?.assets);
  const survivalAnaysisOverview = useAppSelector(
    (state) => state.survivalAnaysisOverview
  );
  const healthScoreData = useAppSelector(
    (state) => state.squaresDashboardReducer.healthScoreData
  );

  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const [selectedAsset, setSelectedAsset] = useState<ServiceView>();
  const [isUploadWorkOrder, setIsUploadWorkOrder] = useState<boolean>(false);
  const [showAssetDetails, setShowAssetDetails] = useState<boolean>(false);
  const [filteredAssets, setFilteredAssets] = useState<ServiceView[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [showView, setShowView] = useState<string | null>(null);

  useEffect(() => {
    const showUploadWorkOrder =
      location.pathname.includes('upload-work-orders');
    setIsUploadWorkOrder(showUploadWorkOrder);
  }, [location.pathname]);

  const handleMonthSelect = (month: string | null) => {
    if (month) {
      setSelectedMonth(month);
    } else {
      setSelectedMonth('');
    }
  };

  const assetsItems = useMemo(() => {
    if (healthScoreData && Object.keys(healthScoreData).length > 0) {
      const assetsArray = Object.values(healthScoreData)
        .map((asset) => ({
          asset_details: asset.asset_details,
          component_details: asset.component_details,
        }))
        .filter((asset) => asset.asset_details?.health_score !== undefined);

      return assetsArray;
    } else {
      console.warn('healthScoreData is empty or invalid.');
      return [];
    }
  }, [healthScoreData]);

  const onShowDetails = (asset: string) => {
    const filterItem = filteredAssets.find(
      (assets) => assets.item_id === asset
    );
    setSelectedAsset(filterItem);
  };

  const handleIsWorkOrder = (check: boolean) => {
    setIsUploadWorkOrder(check);
  };

  const fetchData = async () => {
    try {
      await Promise.all([
        survivalAnalysisHandler.getAssetsRiskAnalysis(),
        survivalAnalysisHandler.getCumulativeAssetsPerSubComponent(),
        survivalAnalysisHandler.getEOLs(),
        squaresHandler.getHealthScoreData(),
        squaresHandler.getPartsRecommendations(new Date(), new Date()),
      ]);
    } catch (error) {
      console.error(error);
    }
  };

  const handleBackClick = () => {
    setShowAssetDetails(false);
  };

  useEffect(() => {
    if (accessToken) {
      void fetchData();
    }
  }, [accessToken]);

  useEffect(() => {
    const assetWithLiveData = processAssetsWithLiveData(
      assets,
      latestTagData,
      notificationsData
    );

    const processedData = processDataAssets(
      survivalAnaysisOverview.assets,
      assetWithLiveData
    );
    setFilteredAssets(processedData);
  }, [survivalAnaysisOverview.assets, assets]);

  const handleModalSuccess = async () => {
    if (!accessToken) {
      await fetchData();
    }
  };

  console.log({ filteredAssets });

  return (
    <>
      {showView && (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              padding: 2,
            }}
          >
            <IconButton
              onClick={() => {
                setShowView(null);
              }}
              sx={{
                marginBottom: 2,
                color: palette.textPrimary,
              }}
            >
              <ArrowBackIcon /> {t('return_to_dashboard')}
            </IconButton>
          </Box>

          {showView === 'totalMaintenanceCost' && (
            <AssetsViewV2 showBackButton={false} />
          )}
          {showView === 'partsReplacement' && <PartsViewV2 />}
          {showView === 'workOrderCount' && <MaintenanceComparisonView />}
          {showView === 'maintenanceHours' && <MaintenanceComparisonView />}
        </>
      )}

      {isUploadWorkOrder && (
        <UploadWorkOrderComponent
          assetsItems={assetsItems}
          handleIsWorkOrder={handleIsWorkOrder}
          handleMonthSelect={handleMonthSelect}
          isUploadWorkOrder={isUploadWorkOrder}
        />
      )}

      {selectedAsset && showAssetDetails && (
        <AssetDetailsView
          setShowAssetDetails={setShowAssetDetails}
          setSelectedAsset={setSelectedAsset}
          selectedAsset={selectedAsset}
          handleBackClick={handleBackClick}
        />
      )}

      {selectedAsset && !showAssetDetails && (
        <OverviewModalDetails
          displayItems={selectedAsset}
          uniqueAssets={filteredAssets}
          onShowDetails={onShowDetails}
          detailType={t('assets')}
          handleModalSuccess={handleModalSuccess}
        />
      )}

      {!showView && !isUploadWorkOrder && !selectedAsset && (
        <MaintenanceDashboardSection
          assetsItems={assetsItems}
          filteredAssets={filteredAssets}
          handleIsWorkOrder={handleIsWorkOrder}
          handleMonthSelect={handleMonthSelect}
          isUploadWorkOrder={isUploadWorkOrder}
          selectedMonth={selectedMonth}
          setShowView={setShowView}
          showDetails={onShowDetails}
          setShowAssetDetails={setShowAssetDetails}
        />
      )}
    </>
  );
};
